import React from "react";
import { Document, Page, Text, View, StyleSheet, Link } from "@react-pdf/renderer";

/* ---------- Styles — dark editorial ---------- */
const GOLD = "#e6c769";
const styles = StyleSheet.create({
  page: {
    backgroundColor: "#06070b",
    color: "#ffffff",
    fontFamily: "Helvetica",
    fontSize: 9.5,
    paddingHorizontal: 44,
    paddingVertical: 42,
  },
  header: {
    borderBottomWidth: 1,
    borderBottomColor: "#1a1e28",
    paddingBottom: 18,
    marginBottom: 20,
  },
  eyebrow: {
    fontFamily: "Courier",
    color: GOLD,
    fontSize: 7.5,
    letterSpacing: 2.2,
    marginBottom: 8,
  },
  name: { fontFamily: "Times-Roman", fontSize: 34, letterSpacing: -0.6, lineHeight: 1.02 },
  role: { color: "#bcbec5", fontSize: 10.5, marginTop: 6 },
  meta: { flexDirection: "row", flexWrap: "wrap", marginTop: 10, fontSize: 8.5, color: "#8a8d97", fontFamily: "Courier" },
  metaItem: { marginRight: 14 },
  section: { marginTop: 16 },
  sectionTitle: {
    fontFamily: "Courier",
    color: GOLD,
    fontSize: 8,
    letterSpacing: 2.5,
    marginBottom: 8,
  },
  sectionH: {
    fontFamily: "Times-Italic",
    fontSize: 18,
    color: "#ffffff",
    marginBottom: 12,
    letterSpacing: -0.3,
  },
  row: { flexDirection: "row", marginBottom: 8 },
  colLabel: { width: 84, fontFamily: "Courier", fontSize: 7.5, color: "#7a7d87", letterSpacing: 1.2, paddingTop: 3 },
  colBody: { flex: 1 },
  itemTitle: { fontSize: 10.5, color: "#ffffff", fontFamily: "Helvetica-Bold" },
  itemSub: { fontSize: 9, color: "#a4a7b0", marginTop: 1.5 },
  itemDetail: { fontSize: 8.8, color: "#8a8d97", marginTop: 3, lineHeight: 1.5 },
  pillRow: { flexDirection: "row", flexWrap: "wrap", marginTop: 4 },
  pill: {
    fontFamily: "Courier",
    fontSize: 7.2,
    letterSpacing: 0.8,
    color: GOLD,
    borderColor: "#3a3120",
    borderWidth: 0.6,
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 10,
    marginRight: 4,
    marginBottom: 3,
  },
  divider: { height: 0.5, backgroundColor: "#1a1e28", marginTop: 14, marginBottom: 4 },
  quote: { fontFamily: "Times-Italic", color: "#d0d2d8", fontSize: 12, marginTop: 8, lineHeight: 1.5 },
  footer: {
    position: "absolute",
    bottom: 26,
    left: 44,
    right: 44,
    borderTopWidth: 0.5,
    borderTopColor: "#1a1e28",
    paddingTop: 8,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 7,
    fontFamily: "Courier",
    color: "#5a5d67",
    letterSpacing: 1.5,
  },
});

/* ---------- Component ---------- */
const EditorialGold = ({ profile, credentials, awards, experiences, projects, skills, places }) => {
  const current = experiences.find((e) => e.current) || experiences[0];

  return (
    <Document
      author={profile.name}
      title={`${profile.name} — Resume`}
      subject="Curriculum Vitae"
      creator="utkarshbokadia.com"
    >
      <Page size="A4" style={styles.page}>
        {/* HEADER */}
        <View style={styles.header}>
          <Text style={styles.eyebrow}>Personal Archive · Résumé</Text>
          <Text style={styles.name}>{profile.name}</Text>
          {current && (
            <Text style={styles.role}>
              {current.role} · {current.company}
            </Text>
          )}
          <View style={styles.meta}>
            <Text style={styles.metaItem}>{profile.location}</Text>
            {profile.email && <Text style={styles.metaItem}>{profile.email}</Text>}
            {profile.phone && <Text style={styles.metaItem}>{profile.phone}</Text>}
            {profile.linkedin && <Link src={profile.linkedin} style={{ ...styles.metaItem, color: GOLD, textDecoration: "none" }}>LinkedIn</Link>}
            <Link src="https://utkarshbokadia.com" style={{ ...styles.metaItem, color: GOLD, textDecoration: "none" }}>utkarshbokadia.com</Link>
          </View>
        </View>

        {/* SUMMARY */}
        {profile.summary && (
          <View style={styles.section}>
            <Text style={styles.quote}>“{profile.summary}”</Text>
          </View>
        )}

        {/* EXPERIENCE */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>01 · EXPERIENCE</Text>
          <Text style={styles.sectionH}>Where I&apos;ve served the work.</Text>
          {experiences.slice(0, 7).map((e) => (
            <View key={e.id} style={styles.row} wrap={false}>
              <Text style={styles.colLabel}>{e.span}</Text>
              <View style={styles.colBody}>
                <Text style={styles.itemTitle}>
                  {e.company}
                  {e.current ? "  · Current" : ""}
                </Text>
                <Text style={styles.itemSub}>
                  {e.role} · {e.location}
                </Text>
                {e.summary && <Text style={styles.itemDetail}>{e.summary}</Text>}
                {Array.isArray(e.skills) && e.skills.length > 0 && (
                  <View style={styles.pillRow}>
                    {e.skills.slice(0, 6).map((s) => (
                      <Text key={s} style={styles.pill}>{s}</Text>
                    ))}
                  </View>
                )}
              </View>
            </View>
          ))}
        </View>

        <View style={styles.divider} />

        {/* PROJECTS */}
        {projects.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>02 · Selected Work</Text>
            <Text style={styles.sectionH}>Built. Shipped. Live.</Text>
            {projects.slice(0, 4).map((p) => (
              <View key={p.id} style={styles.row} wrap={false}>
                <Text style={styles.colLabel}>{p.year}</Text>
                <View style={styles.colBody}>
                  <Text style={styles.itemTitle}>{p.name}</Text>
                  <Text style={styles.itemSub}>
                    {p.tagline}
                    {p.displayUrl ? ` · ${p.displayUrl}` : ""}
                  </Text>
                  {p.mission && <Text style={styles.itemDetail}>{p.mission}</Text>}
                </View>
              </View>
            ))}
          </View>
        )}
      </Page>

      {/* Page 2 — Credentials, Awards, Skills, Journey */}
      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>03 · CREDENTIALS</Text>
          <Text style={styles.sectionH}>The paper trail.</Text>
          {credentials.slice(0, 14).map((c) => (
            <View key={c.id} style={styles.row} wrap={false}>
              <Text style={styles.colLabel}>{c.issued}</Text>
              <View style={styles.colBody}>
                <Text style={styles.itemTitle}>{c.title}</Text>
                <Text style={styles.itemSub}>
                  {c.issuer}
                  {c.credentialId ? ` · ID ${c.credentialId}` : ""}
                </Text>
                {Array.isArray(c.skills) && c.skills.length > 0 && (
                  <View style={styles.pillRow}>
                    {c.skills.slice(0, 5).map((s) => (
                      <Text key={s} style={styles.pill}>{String(s).toUpperCase()}</Text>
                    ))}
                  </View>
                )}
              </View>
            </View>
          ))}
        </View>

        <View style={styles.divider} />

        {awards.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>04 · AWARDS & DISTINCTIONS</Text>
            <Text style={styles.sectionH}>Trophies and moments.</Text>
            {awards.slice(0, 8).map((a) => (
              <View key={a.id} style={styles.row} wrap={false}>
                <Text style={styles.colLabel}>{a.year || a.date}</Text>
                <View style={styles.colBody}>
                  <Text style={styles.itemTitle}>{a.title}</Text>
                  {a.subtitle && <Text style={styles.itemSub}>{a.subtitle}{a.org ? ` · ${a.org}` : ""}</Text>}
                  {a.detail && <Text style={styles.itemDetail}>{a.detail}</Text>}
                </View>
              </View>
            ))}
          </View>
        )}

        <View style={styles.divider} />

        {skills.length > 0 && (
          <View style={styles.section} wrap={false}>
            <Text style={styles.sectionTitle}>05 · TOOLKIT</Text>
            <Text style={styles.sectionH}>How I think.</Text>
            <View style={styles.pillRow}>
              {skills.slice(0, 24).map((s) => (
                <Text key={s.id || s.name} style={styles.pill}>{String(s.name).toUpperCase()}</Text>
              ))}
            </View>
          </View>
        )}

        <View style={styles.divider} />

        {places.length > 0 && (
          <View style={styles.section} wrap={false}>
            <Text style={styles.sectionTitle}>06 · JOURNEY</Text>
            <Text style={styles.sectionH}>Cities that taught me.</Text>
            {places.slice(0, 8).map((p) => (
              <View key={p.id} style={styles.row} wrap={false}>
                <Text style={styles.colLabel}>{p.year}</Text>
                <View style={styles.colBody}>
                  <Text style={styles.itemTitle}>{p.name} · {p.country}</Text>
                  <Text style={styles.itemSub}>{p.phase}</Text>
                </View>
              </View>
            ))}
          </View>
        )}

        {/* FOOTER */}
        <View style={styles.footer} fixed>
          <Text>{profile.name} · Personal Archive</Text>
          <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
};

export default EditorialGold;
